import React, { useEffect, useId, useState } from "react";

import { useTranslation } from "react-i18next";

const NAVY = "#1F2C6B";
const NAVY_LIGHT = "#2F4497";
const GOLD = "#E2B23C";
const INK = "#2F55D4"; // signature ink
const SERIF = "Georgia, 'Times New Roman', Times, serif";
const SANS = "Inter, system-ui, -apple-system, sans-serif";

const CERT = { x: 180, y: 18, w: 585, h: 452 };
const CX = CERT.x + CERT.w / 2;
const CY = CERT.y + CERT.h / 2;
const X0 = CERT.x;
const Y0 = CERT.y;

const CredentialHeroArt = ({ className = "", previewSrc = "" }) => {
  const { t } = useTranslation(["onboarding"]);
  const uid = useId().replace(/:/g, "");
  const id = (name) => `${name}-${uid}`;
  const url = (name) => `url(#${id(name)})`;
  const [previewFailed, setPreviewFailed] = useState(false);
  const showPreview = Boolean(previewSrc) && !previewFailed;

  useEffect(() => {
    setPreviewFailed(false);
  }, [previewSrc]);

  return (
    <>
      {previewSrc ? (
        <img
          src={previewSrc}
          alt=""
          aria-hidden="true"
          className="hidden"
          onError={() => setPreviewFailed(true)}
        />
      ) : null}
      <svg
      viewBox="0 0 960 488"
      role={showPreview ? undefined : "img"}
      aria-hidden={showPreview ? true : undefined}
      aria-label={
        showPreview ? undefined : t("onboarding:welcome_hero_art_alt")
      }
      className={className}
      style={{ width: "100%", height: "auto" }}
    >
      <defs>
        <radialGradient id={id("halo")} cx="50%" cy="50%" r="60%">
          <stop offset="0%" stopColor="#E4E8FB" />
          <stop offset="70%" stopColor="#EEF0FC" />
          <stop offset="100%" stopColor="#F6F7FE" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={id("navy")} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#17235C" />
          <stop offset="100%" stopColor={NAVY_LIGHT} />
        </linearGradient>
        <linearGradient id={id("gold")} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#F6D77A" />
          <stop offset="50%" stopColor={GOLD} />
          <stop offset="100%" stopColor="#C8922A" />
        </linearGradient>
        <linearGradient id={id("photo")} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#E7E2F3" />
          <stop offset="100%" stopColor="#CFC8E6" />
        </linearGradient>
        <filter id={id("soft")} x="-10%" y="-10%" width="120%" height="120%">
          <feGaussianBlur stdDeviation="1.6" />
        </filter>
        <filter id={id("lift")} x="-15%" y="-15%" width="130%" height="135%">
          <feDropShadow
            dx="0"
            dy="16"
            stdDeviation="20"
            floodColor="#1F2C6B"
            floodOpacity="0.16"
          />
        </filter>
        <clipPath id={id("cert")}>
          <rect x={CERT.x} y={CERT.y} width={CERT.w} height={CERT.h} rx="14" />
        </clipPath>
      </defs>

      <rect x="10" y="30" width="940" height="430" rx="80" fill={url("halo")} />

      <g filter={url("soft")} opacity="0.62">
        <rect
          x="34"
          y="150"
          width="300"
          height="292"
          rx="18"
          fill="#FFFFFF"
          stroke="#E6E9F5"
        />
        <circle cx="74" cy="196" r="13" fill="#C9D2F2" />
        <rect x="96" y="188" width="120" height="14" rx="7" fill="#DCE1F4" />
        <rect x="70" y="236" width="150" height="9" rx="4.5" fill="#E7EAF6" />
        <rect x="70" y="256" width="110" height="9" rx="4.5" fill="#E7EAF6" />
        <rect x="80" y="314" width="200" height="98" rx="12" fill="#E9ECF7" />
      </g>

      <g filter={url("soft")} opacity="0.66" transform="rotate(6 785 280)">
        <rect
          x="636"
          y="118"
          width="300"
          height="324"
          rx="18"
          fill="#FFFFFF"
          stroke="#E6E9F5"
        />
        <rect
          x="770"
          y="150"
          width="112"
          height="124"
          rx="12"
          fill={url("photo")}
        />
        <circle cx="826" cy="196" r="24" fill="#B8AFD6" />
        <path d="M786 274c4-30 22-44 40-44s36 14 40 44z" fill="#A79DCB" />
        <rect x="762" y="318" width="128" height="96" rx="12" fill="#F7E7AE" />
      </g>

      <g>
        <rect
          x={CERT.x}
          y={CERT.y}
          width={CERT.w}
          height={CERT.h}
          rx="14"
          fill="#FFFFFF"
          filter={url("lift")}
        />

        {showPreview ? (
          <image
            href={previewSrc}
            x={CERT.x}
            y={CERT.y}
            width={CERT.w}
            height={CERT.h}
            preserveAspectRatio="xMidYMid meet"
            clipPath={url("cert")}
          />
        ) : null}

        {showPreview ? null : (
        <>
        <g clipPath={url("cert")}>
          {[0, 180].map((angle) => (
            <g key={angle} transform={`rotate(${angle} ${CX} ${CY})`}>
              <path
                d={`M${X0} ${Y0}H${X0 + 178}C${X0 + 118} ${Y0 + 16} ${X0 + 52} ${Y0 + 50} ${X0} ${Y0 + 104}Z`}
                fill={url("navy")}
              />
              <path
                d={`M${X0} ${Y0 + 104}C${X0 + 52} ${Y0 + 50} ${X0 + 118} ${Y0 + 16} ${X0 + 178} ${Y0}H${X0 + 198}C${X0 + 132} ${Y0 + 22} ${X0 + 60} ${Y0 + 62} ${X0} ${Y0 + 122}Z`}
                fill={NAVY_LIGHT}
                opacity="0.85"
              />
              <path
                d={`M${X0} ${Y0 + 136}C${X0 + 66} ${Y0 + 70} ${X0 + 142} ${Y0 + 26} ${X0 + 222} ${Y0}`}
                fill="none"
                stroke={url("gold")}
                strokeWidth="4.5"
                strokeLinecap="round"
              />
              <path
                d={`M${X0} ${Y0 + 84}C${X0 + 44} ${Y0 + 42} ${X0 + 98} ${Y0 + 14} ${X0 + 150} ${Y0}`}
                fill="none"
                stroke={url("gold")}
                strokeWidth="1.8"
                strokeLinecap="round"
                opacity="0.9"
              />
            </g>
          ))}
        </g>

        <rect
          x={CERT.x + 20}
          y={CERT.y + 20}
          width={CERT.w - 40}
          height={CERT.h - 40}
          rx="5"
          fill="none"
          stroke={url("gold")}
          strokeWidth="2"
        />
        {[
          [CERT.x + 20, CERT.y + CERT.h - 20],
          [CERT.x + CERT.w - 20, CERT.y + 20],
        ].map(([x, y]) => (
          <circle key={`${x}-${y}`} cx={x} cy={y} r="3.4" fill={url("gold")} />
        ))}

        <g transform={`translate(${CX - 124} 72)`}>
          <g transform="scale(1.18)">
            {/* crown */}
            <path d="M6 10l6 -9 6 7 6 -8 6 8 6 -7 6 9z" fill={url("gold")} />
            <rect
              x="6"
              y="10"
              width="36"
              height="4"
              rx="1.5"
              fill={url("gold")}
            />
            <path
              d="M4 16h40v18c0 13-9 21-20 25C13 55 4 47 4 34z"
              fill={url("navy")}
              stroke={url("gold")}
              strokeWidth="2"
            />
            <path
              d="M24 24v24M17 31h14"
              stroke={url("gold")}
              strokeWidth="3"
              strokeLinecap="round"
            />
          </g>
          <text
            x="62"
            y="45"
            fontFamily={SERIF}
            fontSize="27"
            fontWeight="600"
            fill={NAVY}
          >
            Acme University
          </text>
        </g>

        <text
          x={CX}
          y="194"
          textAnchor="middle"
          fontFamily={SERIF}
          fontSize="44"
          fontWeight="700"
          fill={NAVY}
          letterSpacing="2.2"
        >
          {t("onboarding:certificate_title")}
        </text>
        <text
          x={CX}
          y="220"
          textAnchor="middle"
          fontFamily={SERIF}
          fontSize="17"
          fontWeight="700"
          fill={NAVY}
          letterSpacing="1.6"
        >
          {t("onboarding:certificate_subtitle").toUpperCase()}
        </text>

        <text
          x={CX}
          y="256"
          textAnchor="middle"
          fontFamily={SANS}
          fontSize="13"
          fill="#667085"
        >
          {t("onboarding:certificate_certify")}
        </text>
        <text
          x={CX}
          y="290"
          textAnchor="middle"
          fontFamily={SERIF}
          fontSize="24"
          fontWeight="600"
          fill={NAVY}
        >
          {"{{Student Name}}"}
        </text>

        <path
          d="M246 392c4-20 12-40 22-44c10-4 8 18-4 36c-6 9-10 12-8 6c4-12 18-28 30-30c8-1 2 16 0 22c-1 6 6 0 10-6c5-8 10-12 13-9c3 3-3 13 1 14c4 1 9-9 14-10c6-1 4 9 9 9c6 0 10-8 16-8c7 0 9 6 16 6c10 0 20-4 34-6"
          fill="none"
          stroke={INK}
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M258 404c34-7 74-9 120-5"
          fill="none"
          stroke={INK}
          strokeWidth="1.4"
          strokeLinecap="round"
          opacity="0.75"
        />
        <text
          x="312"
          y="426"
          textAnchor="middle"
          fontFamily={SANS}
          fontSize="13"
          fontWeight="500"
          fill="#344054"
        >
          Dr. Ramesh Kumar
        </text>
        <text
          x="312"
          y="444"
          textAnchor="middle"
          fontFamily={SANS}
          fontSize="12"
          fill="#667085"
        >
          Vice Chancellor
        </text>

        {/* QR code */}
        <g transform="translate(552 318)">
          <rect x="-5" y="-5" width="88" height="88" rx="4" fill="#FFFFFF" />
          <QrPattern size={78} />
        </g>
        <text
          x="591"
          y="420"
          textAnchor="middle"
          fontFamily={SANS}
          fontSize="12"
          fill="#667085"
        >
          {t("onboarding:certificate_scan")}
        </text>
        </>
        )}
      </g>
    </svg>
    </>
  );
};

const QrPattern = ({ size }) => {
  const n = 21;
  const unit = size / n;
  const inFinder = (x, y) =>
    (x < 7 && y < 7) || (x >= n - 7 && y < 7) || (x < 7 && y >= n - 7);

  const cells = [];
  let seed = 13;
  for (let y = 0; y < n; y += 1) {
    for (let x = 0; x < n; x += 1) {
      seed = (seed * 9301 + 49297) % 233280;
      if (!inFinder(x, y) && seed / 233280 > 0.5) {
        cells.push(
          <rect
            key={`${x}-${y}`}
            x={x * unit}
            y={y * unit}
            width={unit}
            height={unit}
            fill="#101828"
          />,
        );
      }
    }
  }

  const finder = (fx, fy) => (
    <g key={`f-${fx}-${fy}`}>
      <rect
        x={fx * unit}
        y={fy * unit}
        width={unit * 7}
        height={unit * 7}
        fill="#101828"
      />
      <rect
        x={(fx + 1) * unit}
        y={(fy + 1) * unit}
        width={unit * 5}
        height={unit * 5}
        fill="#FFFFFF"
      />
      <rect
        x={(fx + 2) * unit}
        y={(fy + 2) * unit}
        width={unit * 3}
        height={unit * 3}
        fill="#101828"
      />
    </g>
  );

  return (
    <g aria-hidden="true" shapeRendering="crispEdges">
      {cells}
      {finder(0, 0)}
      {finder(n - 7, 0)}
      {finder(0, n - 7)}
    </g>
  );
};

export default CredentialHeroArt;
